import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Select, SelectItem, TextInput } from "@carbon/react";
import { Edit } from "@carbon/icons-react";
import { OfferResponse } from "../../types";
import { CURRENCY_LIST } from "../../utils/enums";
import { useUpdateOfferDetails } from "../../hooks/useOffers";
import { useNotification } from "../../context";

type OfferPriceEditorProps = {
  offer: OfferResponse;
};

/**
 * The buyer-side offer price/currency inline editor (Task 7) — read-only price with a pencil to
 * enter edit mode, or the price/currency inputs + save/cancel while editing. Extracted out of
 * `IsBuyerOffer.tsx` and `IsBuyerMultipleOffer.tsx`, which both carried an identical copy of this
 * logic pre-Carbon (the joint file even named its copy `OfferPriceEditor` already) — de-duplicated
 * here rather than migrated twice. Self-contained: owns its own edit-mode state and the
 * `useUpdateOfferDetails` mutation, same as both pre-Carbon copies did, so neither caller's props
 * change.
 */
const OfferPriceEditor = ({ offer }: OfferPriceEditorProps) => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editPrice, setEditPrice] = useState<string>("");
  const [editCurrency, setEditCurrency] = useState<string>("");

  const { mutate: updateOfferDetails, isPending: isUpdating } =
    useUpdateOfferDetails(
      () => {
        openNotification(t("tenders.seller.modifiedOffer"));
        setIsEditMode(false);
      },
      () => {
        openNotification(t("newMaterialForm.error"));
      }
    );

  const enterEditMode = () => {
    setEditPrice(String(offer?.price ?? ""));
    setEditCurrency(offer?.currency ?? "");
    setIsEditMode(true);
  };

  const cancelEdit = () => setIsEditMode(false);

  const saveEdit = () => {
    const priceNumber = parseFloat(editPrice);
    if (!editPrice || isNaN(priceNumber) || priceNumber <= 0 || !editCurrency) {
      return;
    }
    const hasChanges =
      priceNumber !== offer?.price || editCurrency !== offer?.currency;
    if (!hasChanges) {
      setIsEditMode(false);
      return;
    }
    updateOfferDetails({
      offerId: offer.id,
      price: priceNumber,
      currency: editCurrency,
    });
  };

  if (isEditMode) {
    return (
      <div className="offer-price-editor offer-price-editor--editing">
        <Select
          id={`currency-${offer?.id}`}
          size="sm"
          hideLabel
          labelText={t("offer.table.price")}
          disabled={isUpdating}
          value={editCurrency}
          onChange={(e) => setEditCurrency(e.target.value)}
        >
          {CURRENCY_LIST.map((c) => (
            <SelectItem key={c.id} value={c.name} text={c.name} />
          ))}
        </Select>
        <TextInput
          id={`price-${offer?.id}`}
          size="sm"
          type="number"
          hideLabel
          labelText={t("offer.table.price")}
          step="0.01"
          min="0"
          disabled={isUpdating}
          value={editPrice}
          placeholder={String(offer?.price ?? 0)}
          onChange={(e) => setEditPrice(e.target.value)}
        />
        <span className="offer-price-editor__unit">
          / {offer?.order?.measurementUnit}
        </span>
        <Button size="sm" kind="primary" disabled={isUpdating} onClick={saveEdit}>
          {t("offer.save")}
        </Button>
        <Button size="sm" kind="secondary" disabled={isUpdating} onClick={cancelEdit}>
          {t("offer.cancel")}
        </Button>
      </div>
    );
  }

  return (
    <div className="offer-price-editor">
      <span>
        {offer?.price} {offer?.currency} / {offer?.order?.measurementUnit}
      </span>
      {!offer?.selected && (
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          iconDescription={t("offer.edit")}
          renderIcon={Edit}
          onClick={enterEditMode}
        />
      )}
    </div>
  );
};

export default OfferPriceEditor;
